import axios from 'axios';
import { z } from 'zod';

/** Backend spec 008 (sheet D2): opt-in refresh sessions for the web client. */

export const SESSION_MODE_HEADERS = { 'X-Session-Mode': 'refresh' } as const;
const MODE_KEY = 'yodi-session-mode';
const TOKEN_KEY = 'yodi-token';

const refreshResponse = z
  .object({ access_token: z.string().min(1).max(4096), expiresInSec: z.number().int().positive() })
  .strict();

const readMode = () => {
  try {
    return localStorage.getItem(MODE_KEY) === 'refresh';
  } catch {
    return false;
  }
};

export const isRefreshSession = readMode;
export const markRefreshSession = () => localStorage.setItem(MODE_KEY, 'refresh');
/** Any other sign-in (Google, legacy) must never reuse an older refresh cookie. */
export const forgetRefreshSession = () => localStorage.removeItem(MODE_KEY);

/** The `exp` of a JWT in epoch milliseconds, or null when unreadable. */
export function tokenExpiryMs(token: string): number | null {
  try {
    const part = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? '';
    const payload: unknown = JSON.parse(atob(part));
    const exp = typeof payload === 'object' && payload !== null ? Reflect.get(payload, 'exp') : null;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Requests that must never trigger a refresh themselves. */
export const isAuthRoute = (url: string | undefined) => /\/auth\/(login|refresh|logout)\b/.test(url ?? '');

export function createSessionRefresher(baseURL: string) {
  const http = axios.create({ baseURL, withCredentials: true, headers: SESSION_MODE_HEADERS });
  let inFlight: Promise<string | null> | null = null;

  /** One refresh at a time: parallel 401s share it. Null when the session is over. */
  const refresh = (): Promise<string | null> => {
    if (!readMode()) return Promise.resolve(null);
    inFlight ??= http
      .post<unknown>('/auth/refresh')
      .then(({ data }) => {
        const token = refreshResponse.parse(data).access_token;
        localStorage.setItem(TOKEN_KEY, token);
        return token;
      })
      .catch(() => {
        forgetRefreshSession();
        return null;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };

  /** Revokes the server session; idempotent and never blocks the sign-out. */
  const end = () => {
    if (!readMode()) return;
    forgetRefreshSession();
    void http.post('/auth/logout').catch(() => undefined);
  };

  return { refresh, end };
}
